/* =============================================================
   ISD cart state — the one place that decides, from the real Shopify cart:
     - whether a qualifying box is in the cart (product type or tag)
     - which lines are box add-ons (special-price duplicates)
     - the cart weight (Shopify grams × quantity, via cart.total_weight)
     - the delivery state (€45 locker / 10 kg heavy / €75 free for everything)
     - add-on validation: no box → add-on lines are removed, customer is told

   Loaded once from layout/theme.liquid right after the ISD cart API.
   Thresholds come from window.ISD.config (theme settings, one source of truth).

   API
     ISD.state.compute(cart)        -> state (sync; uses cached classifications)
     ISD.state.resolve(cart)        -> Promise<state> (classifies unknown lines first)
     ISD.state.validate()           -> Promise<state> (re-fetches the cart, removes
                                       invalid add-ons, resolves with the final state)
     ISD.state.subscribe(fn)        -> fn(state, cart) on every cart change; returns unsubscribe
     ISD.state.last()               -> last state or null
     ISD.state.fmtWeight(grams)     -> "850 g" / "1,25 kg" / "10 kg"
     ISD.state.text                 -> customer copy (overridable from Liquid)
   Events: document "isd:state" with detail { state, cart, action }
   ============================================================= */
(function () {
  'use strict';
  if (!window.ISD || !window.ISD.cart || window.ISD.state) return;

  var C = window.ISD.config || {};
  var cfg = {
    lockerFreeFrom: Number(C.lockerFreeFrom) || 4500,   /* cents */
    freeAllFrom: Number(C.freeAllFrom) || 7500,         /* cents */
    heavyGrams: Number(C.heavyGrams) || 10000,          /* grams, inclusive */
    courierPrice: Number(C.courierPrice) || 350,        /* cents */
    lockerPrice: Number(C.lockerPrice) || 245,          /* cents */
    boxTag: norm(C.boxTag || 'dezute'),
    boxType: norm(C.boxType || 'Dėžutės'),
    addonTag: norm(C.addonTag || 'box-addon'),
    addonType: norm(C.addonType || 'Dėžutės priedas'),
    addonHandleSuffix: norm(C.addonHandleSuffix || '-prie-dezutes')
  };

  var text = window.ISD.text || {};
  var T = {
    lockerLabel: text.lockerLabel || 'Paštomatas',
    courierLabel: text.courierLabel || 'Kurjeris',
    state1: text.state1 || 'Iki nemokamo pristatymo į paštomatą liko {amount}',
    state2: text.state2 || 'Nemokamas pristatymas į paštomatą',
    state2courier: text.state2courier || 'Kurjeris — {price}',
    state2more: text.state2more || 'Iki nemokamo kurjerio liko {amount}',
    state3: text.state3 || 'Dėl krepšelio svorio galimas tik pristatymas kurjeriu.',
    state3courier: text.state3courier || 'Kurjeris — {price}',
    state3more: text.state3more || 'Iki nemokamo pristatymo liko {amount}',
    state4: text.state4 || 'Jums priklauso nemokamas pristatymas',
    state4heavy: text.state4heavy || 'Nemokamai pristatysime kurjeriu.',
    weightLabel: text.weightLabel || 'Krepšelio svoris',
    addonRemoved: text.addonRemoved || 'Specialios kainos prekėms reikalinga skonio dėžutė. Prekės specialia kaina pašalintos iš krepšelio.',
    addonBlocked: text.addonBlocked || 'Specialios kainos prekėms reikalinga skonio dėžutė.'
  };

  function norm(s) { return String(s || '').toLowerCase().normalize('NFD').replace(/[̀-ͯ]/g, '').trim(); }
  function money(c) { return window.ISD.money(c); }
  function fmtWeight(grams) {
    grams = Math.max(0, Math.round(Number(grams) || 0));
    if (grams < 1000) return grams + ' g';
    var kg = grams / 1000;
    var s = (Math.round(kg * 100) / 100).toFixed(2).replace(/\.?0+$/, '');
    return s.replace('.', ',') + ' kg';
  }

  /* ---------- Line classification (box / addon / other) ---------- */
  var cache = {};          /* product_id -> { box, addon } */
  var pending = {};        /* product_id -> Promise */

  function classifyFrom(type, tags, handle) {
    var t = norm(type), h = norm(handle);
    var tg = (tags || []).map(norm);
    var isBoxType = t && (t === cfg.boxType || t === 'dezutes' || t === 'boxes');
    var isAddonType = t && (t === cfg.addonType || t === 'dezutes priedas' || t === 'box add-on');
    var box = isBoxType || tg.indexOf(cfg.boxTag) > -1;
    var addon = isAddonType || tg.indexOf(cfg.addonTag) > -1 || (cfg.addonHandleSuffix && h.slice(-cfg.addonHandleSuffix.length) === cfg.addonHandleSuffix);
    return { box: !!box && !addon, addon: !!addon };
  }
  function quick(item) {
    /* cart.js has product_type and handle but no tags; decide what we can without a request */
    var t = norm(item.product_type), h = norm(item.handle || '');
    var isBoxType = t && (t === cfg.boxType || t === 'dezutes' || t === 'boxes');
    var isAddonType = t && (t === cfg.addonType || t === 'dezutes priedas' || t === 'box add-on');
    if (isAddonType) return { box: false, addon: true };
    if (cfg.addonHandleSuffix && h.slice(-cfg.addonHandleSuffix.length) === cfg.addonHandleSuffix) return { box: false, addon: true };
    if (isBoxType) return { box: true, addon: false };
    return null;
  }
  function classify(item) {
    var id = item.product_id;
    if (cache[id]) return cache[id];
    var q = quick(item);
    if (q) { cache[id] = q; return q; }
    return null;
  }
  function fetchClass(item) {
    var id = item.product_id;
    if (pending[id]) return pending[id];
    pending[id] = fetch('/products/' + item.handle + '.js', { headers: { 'Accept': 'application/json' } })
      .then(function (r) { return r.ok ? r.json() : null; })
      .then(function (p) { cache[id] = p ? classifyFrom(p.type, p.tags, p.handle) : { box: false, addon: false }; })
      .catch(function () { cache[id] = { box: false, addon: false }; })
      .then(function () { delete pending[id]; });
    return pending[id];
  }
  function resolve(cart) {
    var jobs = [];
    (cart.items || []).forEach(function (it) { if (!classify(it)) jobs.push(fetchClass(it)); });
    return Promise.all(jobs).then(function () { return compute(cart); });
  }

  /* ---------- State ---------- */
  function compute(cart) {
    var items = cart.items || [];
    var boxes = [], addons = [];
    items.forEach(function (it) {
      var c = classify(it) || { box: false, addon: false };
      if (c.box) boxes.push(it);
      if (c.addon) addons.push(it);
    });
    var subtotal = Number(cart.total_price) || 0;              /* after discounts, before shipping */
    var grams = Number(cart.total_weight) || 0;
    var heavy = grams >= cfg.heavyGrams;
    var ship;
    if (subtotal >= cfg.freeAllFrom) {
      ship = { code: 4, target: cfg.freeAllFrom, remaining: 0, progress: 100, complete: true, lockerAvailable: !heavy, courierPrice: 0, lockerPrice: 0,
        title: T.state4, lines: heavy ? [T.state4heavy] : [] };
    } else if (heavy) {
      ship = { code: 3, target: cfg.freeAllFrom, remaining: cfg.freeAllFrom - subtotal, progress: pct(subtotal, cfg.freeAllFrom), complete: false, lockerAvailable: false, courierPrice: cfg.courierPrice, lockerPrice: null,
        title: T.state3, lines: [T.state3courier.replace('{price}', money(cfg.courierPrice)), T.state3more.replace('{amount}', money(cfg.freeAllFrom - subtotal))] };
    } else if (subtotal >= cfg.lockerFreeFrom) {
      ship = { code: 2, target: cfg.freeAllFrom, remaining: cfg.freeAllFrom - subtotal, progress: pct(subtotal, cfg.freeAllFrom), complete: true, lockerAvailable: true, courierPrice: cfg.courierPrice, lockerPrice: 0,
        title: T.state2, lines: [T.state2courier.replace('{price}', money(cfg.courierPrice)), T.state2more.replace('{amount}', money(cfg.freeAllFrom - subtotal))] };
    } else {
      ship = { code: 1, target: cfg.lockerFreeFrom, remaining: cfg.lockerFreeFrom - subtotal, progress: pct(subtotal, cfg.lockerFreeFrom), complete: false, lockerAvailable: true, courierPrice: cfg.courierPrice, lockerPrice: cfg.lockerPrice,
        title: T.state1.replace('{amount}', money(cfg.lockerFreeFrom - subtotal)), lines: [] };
    }
    return {
      subtotal: subtotal, grams: grams, weight: fmtWeight(grams), heavy: heavy,
      hasBox: boxes.length > 0, boxes: boxes, addons: addons,
      invalidAddons: boxes.length === 0 && addons.length > 0,
      shipping: ship, notice: lastNotice, itemCount: cart.item_count || 0
    };
  }
  function pct(a, b) { return b > 0 ? Math.max(0, Math.min(100, Math.round(a / b * 100))) : 0; }

  /* ---------- Enforcement ---------- */
  var lastState = null, lastNotice = '', enforcing = false, listeners = [];
  function emit(state, cart, action) {
    lastState = state;
    listeners.slice().forEach(function (fn) { try { fn(state, cart); } catch (e) { console.warn('[ISD] state listener failed', e); } });
    document.dispatchEvent(new CustomEvent('isd:state', { detail: { state: state, cart: cart, action: action || 'refresh' } }));
    return state;
  }
  function removeAddons(state) {
    /* Remove every add-on line, one request at a time; the cart API emits after each change,
       the enforcing flag keeps those intermediate emits from re-entering here. */
    enforcing = true;
    var keys = state.addons.map(function (i) { return i.key; });
    var chain = Promise.resolve();
    keys.forEach(function (key) { chain = chain.then(function () { return window.ISD.cart.change(key, 0); }); });
    return chain.then(function (cart) { lastNotice = T.addonRemoved; enforcing = false; return cart || window.ISD.cart.get(); })
      .catch(function () { enforcing = false; return window.ISD.cart.get(); });
  }
  function handle(cart, detail) {
    if (enforcing) return;
    var action = detail && detail.action;
    resolve(cart).then(function (state) {
      if (state.invalidAddons) {
        return removeAddons(state).then(function (c2) { return resolve(c2).then(function (s2) { s2.notice = T.addonRemoved; emit(s2, c2, 'enforce'); }); });
      }
      if (action === 'add' || action === 'change') lastNotice = '';
      state.notice = lastNotice;
      emit(state, cart, action);
    });
  }
  window.ISD.cart.subscribe(handle);

  function validate() {
    return window.ISD.cart.get().then(function (cart) {
      return resolve(cart).then(function (state) {
        if (!state.invalidAddons) return state;
        return removeAddons(state).then(function (c2) { return resolve(c2).then(function (s2) { s2.notice = T.addonRemoved; return emit(s2, c2, 'enforce'); }); });
      });
    });
  }

  /* Seed from the server-rendered cart so the first paint needs no request. */
  function seedState() {
    if (lastState) return;
    var seed = document.querySelector('[data-isd-cart-seed]');
    if (seed) { try { var c = JSON.parse(seed.textContent); if (c && c.items) { c.item_count = c.item_count || 0; handle(c, { action: 'seed' }); return; } } catch (e) {} }
    /* No server seed in the DOM yet (it lives in the header block): fall back to one cart request. */
    fetch('/cart.js', { headers: { 'Accept': 'application/json' } }).then(function (r) { return r.ok ? r.json() : null; }).then(function (c) { if (c && c.items && !lastState) handle(c, { action: 'seed' }); }).catch(function () {});
  }
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', seedState); else seedState();

  window.ISD.state = {
    cfg: cfg, text: T, compute: compute, resolve: resolve, validate: validate,
    subscribe: function (fn) { listeners.push(fn); if (lastState) { try { fn(lastState, window.ISD.cart.last()); } catch (e) {} } return function () { listeners = listeners.filter(function (x) { return x !== fn; }); }; },
    last: function () { return lastState; },
    fmtWeight: fmtWeight,
    clearNotice: function () { lastNotice = ''; }
  };
})();
